import { colours } from "@shared/ui.design-system";
import {
  type EmblaState,
  useEmblaPeek,
  useEmblaScrollbar,
  useEmblaState
} from "@shared/ui.hooks";
import { ChevronLeftIcon, ChevronRightIcon } from "@shared/ui.icons";
import { MarkdownContainer, Typography } from "@shared/ui.primitives";
import { useScreenViewport } from "@shared/utils.hooks";
import { clsx } from "clsx";
import useEmblaCarousel, { UseEmblaCarouselType } from "embla-carousel-react";
import { useEffect, useRef } from "react";
import { useInView } from "react-intersection-observer";

import type { PromoTileProps } from "../PromoTile/PromoTile";
import { PromoTile } from "../PromoTile/PromoTile";

import * as styles from "./PromoSection.css";

export type PromoSectionProps = React.ComponentProps<"article"> & {
  title: string;
  description: string;
  promos?: PromoTileProps[] | undefined;
  variant: "pdp" | "basket";
  carouselTracking?: {
    previousChevronClicked: () => void;
    nextChevronClicked: () => void;
  };
};

const CarouselControls = ({
  emblaState,
  carouselTracking,
  ...rest
}: React.ComponentProps<"nav"> & {
  emblaState: EmblaState;
  carouselTracking?: {
    previousChevronClicked: () => void;
    nextChevronClicked: () => void;
  };
}) => {
  return (
    <nav aria-label="Carousel controls" {...rest}>
      <button
        disabled={!emblaState.hasPrev}
        aria-controls="promo-carousel"
        aria-label="Previous slide"
        className={styles.carousel.controls.button}
        onClick={() => {
          emblaState.goTo("previous");
          carouselTracking?.previousChevronClicked();
        }}
      >
        <ChevronLeftIcon
          color={colours.primary.dark}
          height={20}
          width={20}
          style={{ transform: "translateX(-1px)" }}
        />
      </button>
      <button
        disabled={!emblaState.hasNext}
        aria-controls="promo-carousel"
        aria-label="Next slide"
        className={styles.carousel.controls.button}
        onClick={() => {
          emblaState.goTo("next");
          carouselTracking?.nextChevronClicked();
        }}
      >
        <ChevronRightIcon
          color={colours.primary.dark}
          height={20}
          width={20}
          style={{ transform: "translateX(2px)" }}
        />
      </button>
    </nav>
  );
};

const CarouselScrollbar: React.FC<{
  emblaApi: UseEmblaCarouselType[1];
  className?: string;
}> = ({ emblaApi, className }) => {
  const scrollbarContainerRef = useRef<HTMLDivElement | null>(null);
  const { showCue, thumbWidth, thumbTranslateX } = useEmblaScrollbar({
    emblaApi,
    scrollbarContainerRef
  });

  if (!showCue) return null;

  return (
    <div
      className={clsx(styles.carousel.scrollbar.container, className)}
      ref={scrollbarContainerRef}
      aria-hidden="true"
      data-testid="carousel-scrollbar"
    >
      <div
        className={styles.carousel.scrollbar.thumb}
        style={{
          width: `${thumbWidth}px`,
          transform: `translateX(${thumbTranslateX}px)`
        }}
      ></div>
    </div>
  );
};

export const PromoSection = ({
  title,
  description,
  promos = [],
  variant = "pdp",
  className,
  carouselTracking,
  ...rest
}: PromoSectionProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    duration: 20,
    align: "start",
    containScroll: "trimSnaps",
    dragFree: false
  });
  const emblaState = useEmblaState(emblaApi);
  const { triggerPeek, isPeeking } = useEmblaPeek({
    emblaApi,
    fireOnceKey: "promo-section"
  });

  const { ref, inView } = useInView({ threshold: 1 });
  const { isMobile } = useScreenViewport();

  useEffect(() => {
    if (inView && isMobile) triggerPeek();
  }, [inView, isMobile, triggerPeek]);

  if (!promos || !Array.isArray(promos) || !promos?.length) return null;

  const withSectionIntro = variant === "pdp" && (!!title || !!description);
  const withInternalControls = promos.length > 1 && variant === "pdp";
  const withExternalControls = promos.length > 2 && variant === "basket";

  const getCount = () => {
    if (promos.length === 1) return "single";
    if (promos.length === 2 && variant === "basket") return "double";
    if (promos.length > 2 && variant === "basket") return "multiple";
    return undefined;
  };

  const safePromoTracking = (
    promoTracking: PromoTileProps["tracking"]
  ): PromoTileProps["tracking"] => {
    if (isPeeking && promoTracking)
      return {
        ...promoTracking,
        viewed: () => {}
      };

    return promoTracking;
  };

  return (
    <article
      ref={ref}
      data-test-id="promo-section"
      aria-labelledby="promo-carousel-heading"
      className={clsx(styles.container({ variant }), className)}
      {...rest}
    >
      {withSectionIntro && (
        <section className={styles.panelIntro}>
          <div className={styles.sectionIntro}>
            {title && (
              <Typography
                as="h2"
                id="promo-carousel-heading"
                variant="subheader-4"
                colour="primary-dark"
              >
                {title}
              </Typography>
            )}
            {description && (
              <span className={styles.srOnlyMobile}>
                <MarkdownContainer
                  content={description}
                  forceBlock
                  tagOverrides={{
                    p: {
                      component: Typography,
                      props: {
                        as: "p",
                        variant: "body-sm",
                        colour: "primary-medium"
                      }
                    }
                  }}
                />
              </span>
            )}
          </div>
          {withInternalControls && (
            <CarouselControls
              emblaState={emblaState}
              carouselTracking={carouselTracking}
              className={clsx(
                styles.carousel.controls.container,
                styles.srOnlyMobile
              )}
            />
          )}
        </section>
      )}

      {withExternalControls && (
        <CarouselControls
          emblaState={emblaState}
          carouselTracking={carouselTracking}
          className={clsx(
            styles.carousel.controls.container,
            styles.carousel.controls.external
          )}
        />
      )}

      <section
        className={styles.emblaWrapper}
        aria-labelledby="promo-carousel-heading"
        id="promo-carousel"
        role="region"
        aria-roledescription="carousel"
        aria-label="Promotional offers"
      >
        <div className={styles.emblaViewport} ref={emblaRef}>
          <div className={styles.emblaContainer}>
            {promos.map(({ tracking, ...promoTileProps }, index) => (
              <div
                className={styles.emblaSlide({ count: getCount(), variant })}
                key={promoTileProps.title}
                role="group"
                aria-roledescription="slide"
                aria-label={`Slide ${index + 1} of ${promos.length}`}
              >
                <PromoTile
                  {...promoTileProps}
                  tracking={safePromoTracking(tracking)}
                  variant="compact"
                />
              </div>
            ))}
          </div>
        </div>

        {withInternalControls && withSectionIntro && (
          <nav
            aria-label="Carousel pagination"
            className={clsx(
              styles.carousel.pagination.container,
              styles.srOnlyMobile
            )}
          >
            {promos.map(({ title }, index) => (
              <button
                aria-label={`Go to slide ${index + 1} of ${promos.length}`}
                aria-current={
                  emblaState.currentIndex === index ? "true" : undefined
                }
                disabled={emblaState.currentIndex === index}
                key={`${title}-${index}`}
                className={styles.carousel.pagination.button}
                onClick={() => {
                  emblaState.goTo(index);
                }}
              ></button>
            ))}
          </nav>
        )}

        <CarouselScrollbar
          emblaApi={emblaApi}
          className={variant === "pdp" ? styles.hiddenDesktop : undefined}
        />
      </section>
    </article>
  );
};
